<template>
    <div>
        <SendingTaskParametersDialog
            :total-delivery-weight="totalDeliveryWeight"
            :is-open="sendingParametersDialog"
            :task-data="task"
            :imposition-packages="impositionTaskPackages"
            :delivery-methods="deliveryMethods"
            @on-close="closeSendingParametersDialog"
            @on-save="saveSendingParametersDialog"
        />

        <v-dialog
            :value="isOpen"
            scrollable
            max-width="900px"
            transition="slide-y-transition"
            eager
            :persistent="actionLoading"
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card v-if="hasData">
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        Parametry procesu: <strong>{{ task.process.name }}</strong>
                    </h1>
                </v-card-title>

                <v-card-text class="pa-0">
                    <v-container fluid class="px-6 py-2">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="task.machine"
                                    :items="filterMachinesByTask(task, machines)"
                                    label="Maszyna"
                                    item-text="name"
                                    return-object
                                    outlined
                                    dense
                                    hide-details
                                    :error="machineErrors"
                                    :disabled="!isJobEditable"
                                    @change="machineChanged"
                                />
                            </v-col>
                        </v-row>

                        <v-row v-if="isFoldingTask && task.machine">
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="task.foldingTypeId"
                                    :items="availableFoldingTypes"
                                    label="Sposób falcowania"
                                    item-text="name"
                                    item-value="id"
                                    outlined
                                    dense
                                    hide-details
                                    :error="foldingTypeErrors"
                                    :disabled="!isJobEditable"
                                />
                            </v-col>
                        </v-row>

                        <v-row v-if="isCuttingPaperTask">
                            <v-col cols="12" sm="4">
                                <v-autocomplete
                                    v-model="imposition.paper"
                                    label="Rodzaj papieru"
                                    :items="papers"
                                    item-text="name"
                                    return-object
                                    outlined
                                    dense
                                    hide-details
                                    :disabled="!isJobEditable"
                                    @change="paperChanged"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="imposition.paperDescription"
                                    label="Nazwa papieru"
                                    dense
                                    outlined
                                    counter="256"
                                    :error="paperDescriptionErrors"
                                    :disabled="!isJobEditable"
                                />
                            </v-col>
                        </v-row>

                        <v-row v-if="isLaminationTask">
                            <v-col cols="12" md="3">
                                <v-text-field
                                    :value="imposition.formatA"
                                    label="Format papieru A"
                                    suffix="mm"
                                    dense
                                    outlined
                                    hide-details
                                    :readonly="!isJobEditable"
                                    :disabled="isJobEditable"
                                />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    :value="imposition.formatB"
                                    label="Format papieru B"
                                    suffix="mm"
                                    dense
                                    outlined
                                    hide-details
                                    :readonly="!isJobEditable"
                                    :disabled="isJobEditable"
                                />
                            </v-col>
                        </v-row>

                        <v-row v-if="!isSendingTask">
                            <v-col v-if="isSewnBindTask" cols="12" md="6">
                                <v-autocomplete
                                    :value="selectedMaterial"
                                    :items="materials"
                                    item-text="name"
                                    item-value="id"
                                    label="Rodzaj zszywki"
                                    dense
                                    outlined
                                    hide-details
                                    :loading="actionLoading"
                                    :disabled="!isJobEditable"
                                    :error="sewnBindMaterialErrors"
                                    return-object
                                    @input="updateMaterial"
                                >
                                    <template #selection="{item}">
                                        {{ item.name }}
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col v-else-if="isGlueBindTask" cols="12" md="6">
                                <v-autocomplete
                                    :value="selectedMaterial"
                                    :items="materials"
                                    item-text="name"
                                    item-value="id"
                                    label="Klej"
                                    dense
                                    outlined
                                    hide-details
                                    :loading="actionLoading"
                                    :disabled="!isJobEditable"
                                    :error="glueBindMaterialErrors"
                                    return-object
                                    @input="updateMaterial"
                                >
                                    <template #selection="{item}">
                                        {{ item.name }}
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col v-else-if="isTaskParameterAvailable(task, 'calculationImpositionTaskMaterials')" cols="12" md="6">
                                <v-text-field
                                    :value="parseMaterialsValue(task.impositionTaskMaterial)"
                                    readonly
                                    label="Materiały"
                                    dense
                                    outlined
                                    append-icon="mdi-cog"
                                    hide-details
                                    :loading="actionLoading"
                                    :disabled="!isJobEditable"
                                    @click="showMaterialsDialog(task)"
                                    @click:append="showMaterialsDialog(task)"
                                />
                            </v-col>
                            <v-col
                                v-for="property in task.impositionTaskProperties"
                                :key="property.id"
                                :md="getColumnWidth(task)"
                                cols="12"
                            >
                                <v-checkbox
                                    v-if="isCheckboxProperty(property)"
                                    :input-value="parseCheckboxValue(property.value)"
                                    :label="property.productionProcessProperty.name | capitalize"
                                    class="ma-0 pa-0"
                                    hide-details
                                    :disabled="!isJobEditable"
                                    :error="isSewnBindTask && propertyErrors(getPropertyIndex(property))"
                                    @change="changeCheckboxValue(property, $event)"
                                />
                                <v-autocomplete
                                    v-else-if="property.productionProcessProperty.productionProcessPropertyValues.length > 0"
                                    v-model="property.value"
                                    :items="property.productionProcessProperty.productionProcessPropertyValues"
                                    :label="property.productionProcessProperty.name | capitalize"
                                    :suffix="property.productionProcessProperty.defaultUnit"
                                    item-value="name"
                                    item-text="name"
                                    dense
                                    outlined
                                    hide-details
                                    :disabled="!isJobEditable"
                                    :error="isSewnBindTask && propertyErrors(getPropertyIndex(property))"
                                    clearable
                                />
                                <v-text-field
                                    v-else
                                    v-model="property.value"
                                    :label="property.productionProcessProperty.name | capitalize"
                                    :suffix="property.productionProcessProperty.defaultUnit"
                                    dense
                                    outlined
                                    :disabled="!isJobEditable"
                                    hide-details
                                />
                            </v-col>
                        </v-row>

                        <v-row v-if="isCuttingPaperTask">
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-mask="'#####'"
                                    :value="imposition.formatA"
                                    label="Format A na wyjściu"
                                    dense
                                    outlined
                                    :hint="impositionFormatHint(imposition)"
                                    persistent-hint
                                    :hide-details="!isJobEditable"
                                    :error="impositionFormatAErrors()"
                                    :readonly="!isJobEditable"
                                    @input="handleFormatA"
                                    @change="changeFormatA"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-mask="'#####'"
                                    :value="imposition.formatB"
                                    label="Format B na wyjściu"
                                    dense
                                    outlined
                                    :hint="impositionFormatHint(imposition)"
                                    persistent-hint
                                    :hide-details="!isJobEditable"
                                    :error="impositionFormatBErrors()"
                                    :readonly="!isJobEditable"
                                    @input="handleFormatB"
                                    @change="changeFormatB"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="isCtpTask">
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    :value="parseColorsValue(imposition.impositionColoursA)"
                                    readonly
                                    label="Kolorystyka A"
                                    dense
                                    outlined
                                    disabled
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    :value="parseColorsValue(imposition.impositionColoursB)"
                                    readonly
                                    label="Kolorystyka B"
                                    dense
                                    outlined
                                    disabled
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="isCtpTask">
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="imposition.plateTypeId"
                                    label="Rodzaj płyty"
                                    :items="plateTypes"
                                    item-text="name"
                                    item-value="id"
                                    outlined
                                    :error="plateTypeIdErrors"
                                    hide-details
                                    dense
                                    :disabled="!isJobEditable"
                                />
                            </v-col>
                        </v-row>
                        <template v-if="isSlicingAfterPrintingTask || isSlicingAfterFinishingTask || isSlicingAfterDieCuttingTask">
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-mask="'#####'"
                                        :value="imposition.formatA"
                                        label="Format A"
                                        suffix="mm"
                                        dense
                                        outlined
                                        :hint="impositionFormatHint(imposition)"
                                        persistent-hint
                                        :hide-details="!isJobEditable"
                                        :error="impositionFormatAErrors()"
                                        disabled
                                        @input="handleFormatA(imposition, $event)"
                                        @change="changeFormatA(imposition, $event)"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-mask="'#####'"
                                        :value="imposition.formatB"
                                        label="Format B"
                                        suffix="mm"
                                        dense
                                        outlined
                                        :hint="impositionFormatHint(imposition)"
                                        persistent-hint
                                        :hide-details="!isJobEditable"
                                        :error="impositionFormatBErrors()"
                                        disabled
                                        @input="handleFormatB(imposition, $event)"
                                        @change="changeFormatB(imposition, $event)"
                                    />
                                </v-col>
                            </v-row>
                        </template>
                        <template v-if="isSlicingAfterPrintingTask || isSlicingAfterFinishingTask || isSlicingAfterDieCuttingTask">
                            <v-row v-for="(dieCutter, dieCutterIndex) in impositionDieCutters" :key="dieCutterIndex">
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="dieCutter.formatA"
                                        v-mask="'#####'"
                                        :label="getLabelWithIndex(dieCutterIndex, 'Format A na wyjściu')"
                                        suffix="mm"
                                        dense
                                        outlined
                                        persistent-hint
                                        :hide-details="!isJobEditable"
                                        :error="impositionDieCutterFormatAErrors(dieCutterIndex)"
                                        :readonly="!isJobEditable"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="dieCutter.formatB"
                                        v-mask="'#####'"
                                        :label="getLabelWithIndex(dieCutterIndex, 'Format B na wyjściu')"
                                        suffix="mm"
                                        dense
                                        outlined
                                        persistent-hint
                                        :hide-details="!isJobEditable"

                                        :error="impositionDieCutterFormatBErrors(dieCutterIndex)"
                                        :readonly="!isJobEditable"
                                    />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        v-model="dieCutter.partAmount"
                                        v-mask="'##'"
                                        label="Ilość użytków po krojeniu"
                                        dense
                                        outlined
                                        hide-details
                                        :error="impositionDieCutterPartAmountErrors(dieCutterIndex)"
                                        :readonly="!isJobEditable"
                                    />
                                </v-col>
                                <v-col cols="12" md="1">
                                    <div class="d-inline-flex">
                                        <v-tooltip
                                            v-if="!dieCutterIndex"
                                            :open-delay="tooltipDelay"
                                            top
                                        >
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    :disabled="impositionDieCutters.length > 1"
                                                    v-on="on"
                                                    @click="addDieCutter()"
                                                >
                                                    <v-icon color="green">
                                                        mdi-plus
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Dodaj wykrojnik</span>
                                        </v-tooltip>
                                        <v-tooltip
                                            v-if="dieCutterIndex > 0"
                                            :open-delay="tooltipDelay"
                                            top
                                        >
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    v-on="on"
                                                    @click="removeDieCutter(dieCutterIndex)"
                                                >
                                                    <v-icon color="red">
                                                        mdi-delete
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Usuń</span>
                                        </v-tooltip>
                                    </div>
                                </v-col>
                            </v-row>
                        </template>
                        <template v-if="isDieCuttingTask">
                            <v-row
                                v-for="(dieCutter, dieCutterIndex) in impositionDieCutters"
                                :key="dieCutterIndex + '_dieCutter'"
                            >
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="dieCutter.owner"
                                        :items="dieCuttersOwnershipTypes"
                                        :label="getLabelWithIndex(dieCutterIndex, 'Wykrojnik')"
                                        item-value="id"
                                        item-text="name"
                                        dense
                                        outlined
                                        return-object
                                        hide-details
                                        :error="dieCutterOwnerErrors(dieCutterIndex)"
                                        :readonly="!isJobEditable"
                                        @change="setDieCutterOwner(dieCutterIndex, $event)"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-if="isNewDieCutter(dieCutterIndex)"
                                        v-model="dieCutter.newDieCutterSymbol"
                                        :label="getLabelWithIndex(dieCutterIndex, 'Numer Wykrojnika')"
                                        dense
                                        outlined
                                        hide-details
                                        :readonly="!isJobEditable"
                                        :clearable="isJobEditable"
                                    />
                                    <v-autocomplete
                                        v-if="!isNewDieCutter(dieCutterIndex)"
                                        v-model="dieCutter.dieCutter"
                                        :items="dieCutters"
                                        :label="getLabelWithIndex(dieCutterIndex, 'Numer Wykrojnika')"
                                        item-value="id"
                                        item-text="symbol"
                                        no-data-text="Brak danych..."
                                        dense
                                        outlined
                                        return-object
                                        hide-details
                                        :error="numberDieCutterErrors(dieCutterIndex)"
                                        :clearable="isJobEditable"
                                        :readonly="!isJobEditable"
                                        :disabled="isDieCutterDisabled(dieCutterIndex)"
                                        @change="setDieCutterType(dieCutterIndex, $event)"
                                    />
                                </v-col>
                            </v-row>
                            <v-row
                                v-for="(dieCutterFormat, dieCutterIndexFormat) in impositionDieCutters"
                                :key="dieCutterIndexFormat + '_formats'"
                            >
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :value="dieCutterFormat.formatA"
                                        :label="getLabelWithIndex(dieCutterIndexFormat, 'Format A')"
                                        suffix="mm"
                                        dense
                                        outlined
                                        disabled
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :value="dieCutterFormat.formatB"
                                        :label="getLabelWithIndex(dieCutterIndexFormat, 'Format B')"
                                        suffix="mm"
                                        dense
                                        outlined
                                        disabled
                                        hide-details
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col
                                    v-for="(dieCutterPrice, dieCutterIndexPrice) in impositionDieCutters"
                                    :key="dieCutterIndexPrice"
                                    cols="12"
                                    md="6"
                                >
                                    <v-text-field
                                        :value="dieCutterPrice.price"
                                        :label="getLabelWithIndex(dieCutterIndexPrice, 'Cena wykrojnika')"
                                        dense
                                        outlined
                                        suffix="zł"
                                        hide-details
                                        :error="impositionDieCutterPriceErrors(dieCutterIndexPrice)"
                                        :readonly="!isJobEditable"
                                        :disabled="isDieCutterDisabled(dieCutterIndexPrice)"
                                        required
                                        @input="inputDieCutterPrice(dieCutterIndexPrice, $event)"
                                        @change="setDieCutterPrice(dieCutterIndexPrice, $event)"
                                    />
                                </v-col>
                            </v-row>
                        </template>

                        <v-row v-if="isSendingTask">
                            <v-col cols="12" md="7">
                                <v-text-field
                                    :value="getDeliveryMethodName(task.deliveryMethodId)"
                                    label="Transport"
                                    outlined
                                    dense
                                    hide-details
                                    readonly
                                />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    :value="getTransportDistanceTaskProperty().value"
                                    item-value="name"
                                    item-text="name"
                                    label="Dystans"
                                    :suffix="distanceSuffix(task)"
                                    outlined
                                    dense
                                    hide-details
                                    :error="propertyErrors(1)"
                                    readonly
                                />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    :value="getTransportCostTaskProperty().value"
                                    item-value="name"
                                    item-text="name"
                                    label="Koszt"
                                    outlined
                                    suffix="zł"
                                    dense
                                    hide-details
                                    :error="propertyErrors(0)"
                                    readonly
                                />
                            </v-col>
                            <v-col cols="12" md="1">
                                <div class="d-inline-flex">
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                v-on="on"
                                                @click="showSendingParametersDialog()"
                                            >
                                                <v-icon color="blue">
                                                    mdi-pencil
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Edytuj</span>
                                    </v-tooltip>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row v-if="!isPackingTask && !isSendingTask && !isCtpTask">
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="task.wasteAmount"
                                    label="Straty"
                                    suffix="szt."
                                    outlined
                                    dense
                                    :error-messages="taskWasteAmountErrors"
                                    required
                                    :disabled="!isJobEditable"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="task.wastePercent"
                                    label="Straty"
                                    suffix="%"
                                    outlined
                                    dense
                                    :error-messages="taskWastePercentErrors"
                                    required
                                    :disabled="!isJobEditable"
                                />
                            </v-col>
                        </v-row>


                        <v-row>
                            <v-col>
                                <v-textarea
                                    :value="task.description"
                                    label="Uwagi do procesu"
                                    auto-grow
                                    rows="1"
                                    dense
                                    outlined
                                    clearable
                                    hide-details
                                    :disabled="!isJobEditable"
                                    @input="parseDescriptionValue"
                                />
                            </v-col>
                        </v-row>

                        <v-row v-if="isSlicingAfterPrintingTask || isSlicingAfterFinishingTask || isSlicingAfterDieCuttingTask">
                            <v-col cols="12">
                                <v-data-table
                                    :items="task.impositionTaskOrderComponents"
                                    :headers="formatsHeaders"
                                    disable-pagination
                                    :mobile-breakpoint="0"
                                    hide-default-footer
                                >
                                    <template #item.componentName="{item}">
                                        <span>{{ item.orderProductComponent.name }}</span>
                                    </template>
                                    <template #item.outputFormatA="{item}">
                                        {{ item.outputFormatA ? `${item.outputFormatA} mm` : '-' }}
                                    </template>
                                    <template #item.outputFormatB="{item}">
                                        {{ item.outputFormatB ? `${item.outputFormatB} mm` : '-' }}
                                    </template>
                                    <template #item.actions="{item}">
                                        <div class="d-inline-flex">
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        v-on="on"
                                                        @click="showSlicingFormatsDialog(item)"
                                                    >
                                                        <v-icon color="blue">
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Edytuj</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                </v-data-table>
                            </v-col>
                        </v-row>
                    </v-container>

                    <v-divider v-if="!isCuttingPaperTask" />

                    <v-container v-if="!isCuttingPaperTask" fluid class="px-6 py-2">
                        <v-row>
                            <v-col cols="12">
                                <h1 class="text-subtitle-1">
                                    Komponenty
                                </h1>
                                <v-data-table
                                    :headers="componentsHeaders"
                                    :items="task.impositionTaskOrderComponents"
                                    :mobile-breakpoint="0"
                                    hide-default-footer
                                    disable-pagination
                                >
                                    <template #item.productionNumber="{item}">
                                        {{ item.orderProductComponent.productionNumber }}
                                    </template>
                                    <template #item.productName="{item}">
                                        {{ item.orderProductComponent.productName }}
                                    </template>
                                    <template #item.foldingType="{item}">
                                        {{ item.foldingType }}
                                    </template>
                                    <template #item.componentName="{item}">
                                        {{ item.orderProductComponent.name }}
                                    </template>
                                    <template #item.componentFormat="{item}">
                                        {{ item.orderProductComponent.componentFormat }}
                                    </template>
                                    <template #item.parts="{item}">
                                        {{ item.parts }}
                                    </template>
                                    <template #item.nettoAmount="{item}">
                                        {{ item.nettoAmount }}
                                    </template>
                                </v-data-table>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <template v-if="isJobEditable">
                        <v-btn
                            color="error"
                            text
                            data-sentry-label="Anuluj"
                            @click="onClose"
                        >
                            Anuluj
                        </v-btn>
                        <v-spacer />
                        <v-btn
                            color="success"
                            text
                            data-sentry-label="Zapisz"
                            @click="onSave"
                        >
                            Zapisz
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn
                            color="error"
                            text
                            data-sentry-label="Zamknij"
                            @click="onClose"
                        >
                            Zamknij
                        </v-btn>
                    </template>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import SendingTaskParametersDialog from './SendingTaskParametersDialog'

import { appConfig } from '@/config'

import { errorHandler } from '@/utils/errorHandler.js'
import { fourDigitsAfterDecimal, twoDigitsAfterDecimal, twoDigitsAfterDecimalWithComma } from '@/utils/customValidators'

import tooltipDelay from '@/mixins/tooltip'
import formats from './mixins/formats'
import parse from '@/mixins/parse'
import priceFormatter from '@/mixins/priceFormatter'

import {
    required,
    integer,
    between,
    alpha,
    decimal,
    minValue,
    maxLength,
    helpers,
    requiredIf
} from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'

export default {
    name: 'TaskParametersDialog',
    directives: { mask },
    components: { SendingTaskParametersDialog },
    mixins: [ formats, tooltipDelay, parse, priceFormatter ],
    props: {
        isOpen: {
            type: Boolean
        },
        jobId: {
            type: Number,
            default: null
        },
        jobStatusId: {
            type: Number,
            default: null
        },
        impositionData: {
            type: Object,
            default: null
        },
        taskData: {
            type: Object,
            default: null
        },
        plateTypesData: {
            type: Array,
            default: null
        },
        machines: {
            type: Array,
            default: null
        },
        papers: {
            type: Array,
            default: null
        },
        isJobEditable: {
            type: Boolean
        },
        actionLoading: {
            type: Boolean
        },
        totalDeliveryWeight: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            imposition: {
                paper: {
                    name: null
                },
                paperId: null,
                paperDescription: null,
                formatA: null,
                formatB: null,
                plateTypeId: null
            },
            task: {
                process: {
                    name: null
                },
                impositionTaskMaterial: [],
                requiredFields: [],
                foldingTypeId: null,
            },
            plateTypes: [],
            sendingParametersDialog: false,
            formatsHeaders: [
                {
                    text: 'Nazwa komponentu',
                    value: 'componentName',
                    align: 'left',
                    sortable: false,
                    width: 200,
                    showOnReadOnly: true
                },
                {
                    text: 'Format A wyjścia',
                    value: 'outputFormatA',
                    align: 'center',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Format B wyjścia',
                    value: 'outputFormatB',
                    align: 'center',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 80,
                    showOnReadOnly: false
                },
            ],
            deliveryMethods: [],
            materials: [],
            selectedMaterial: null,
        }
    },
    validations() {
        const impositionFormatRangeHelper = () => {
            return helpers.withParams({ type: 'impositionFormatRangeHelper' }, (value, impositionModel) => {
                if (impositionModel.impositionTask.length > 0) {
                    const { formatARangeX, formatARangeY, formatBRangeX, formatBRangeY } = this.formatRangeValidations

                    return this.hasImpositionPrintTask ? (formatARangeX && formatBRangeY) || (formatARangeY && formatBRangeX) : true
                } else {
                    return true
                }
            })
        }

        const taskPropertiesHelper = () => {
            return helpers.withParams({ type: 'taskPropertiesHelper' }, (propertyModel) => {

                const validators = {
                    required: required,
                    integer: integer,
                    alpha: alpha,
                    decimal: decimal,
                    greaterThanZero: minValue(0.01),
                }

                const requirements = propertyModel.productionProcessProperty.validators,
                    validations = []

                requirements.forEach(requirement => {
                    if (validators[requirement.name]) {
                        const isValid = validators[requirement.name](propertyModel.value)


                        validations.push({ requirement, isValid })
                    }
                })

                return validations.every(item => item.isValid)
            })
        }

        const requiredDieCutterFieldHelper = (field) => {
            return helpers.withParams({ type: 'requiredDieCutterFieldHelper' }, (value, model) => {

                if (!this.isDieCuttingTask || !this.isSlicingAfterPrintingTask || !this.isSlicingAfterFinishingTask || !this.isSlicingAfterDieCuttingTask) {
                    return true
                }

                // if dieCutter is new or from client, the price and dieCutter are not required
                if (['dieCutter', 'price'].includes(field) && model.owner !== 1) {
                    return true
                }

                const validators = {
                    partAmount: required,
                    dieCutter: required,
                    owner: required,
                    formatA: required,
                    formatB: required,
                    price: [ required, twoDigitsAfterDecimalWithComma ]
                }

                return Array.isArray(validators[field]) ? validators[field].every(i => i(value)) : validators[field](value)
            })
        }

        return {
            imposition: {
                formatA: {
                    isValid: impositionFormatRangeHelper()
                },
                formatB: {
                    isValid: impositionFormatRangeHelper()
                },
                paperDescription: {
                    maxLength: maxLength(256)
                },
                plateTypeId: {
                    required
                },
                impositionDieCutters: {
                    $each: {
                        partAmount: {
                            isValid: requiredDieCutterFieldHelper('partAmount'),
                        },
                        formatA: {
                            isValid: requiredDieCutterFieldHelper('formatA'),
                        },
                        formatB: {
                            isValid: requiredDieCutterFieldHelper('formatB'),
                        },
                        dieCutter: {
                            isValid: requiredDieCutterFieldHelper('dieCutter'),
                        },
                        owner: {
                            isValid: requiredDieCutterFieldHelper('owner'),
                        },
                        price: {
                            isValid: requiredDieCutterFieldHelper('price'),
                        }
                    }
                }
            },
            task: {
                machineId: { required },
                wasteAmount: {
                    required,
                    integer
                },
                wastePercent: {
                    required,
                    fourDigitsAfterDecimal,
                    between: between(0, appConfig.jobs.maxWastePercent)
                },
                impositionTaskProperties: {
                    $each: {
                        isValid: taskPropertiesHelper()
                    }
                },
                foldingTypeId: {
                    required: requiredIf(function() {
                        return this.isFoldingTask
                    })
                },
                impositionTaskMaterial: {
                    required: requiredIf(function() {
                        return this.isSewnBindTask || this.isGlueBindTask
                    })
                }
            }
        }
    },
    computed: {
        componentsHeaders() {
            const baseHeaders = [
                {
                    text: 'Nr zamówienia',
                    value: 'productionNumber',
                    sortable: false
                },
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    sortable: false
                },
                {
                    text: 'Rodzaj składania',
                    value: 'foldingType',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Komponent',
                    value: 'componentName',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Format',
                    value: 'componentFormat',
                    sortable: false,
                    align: 'center'
                }
            ]
            
            baseHeaders.push(
                {
                    text: 'Użytki',
                    value: 'parts',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Nakład netto',
                    value: 'nettoAmount',
                    sortable: false,
                    align: 'center'
                }
            )
        
            return baseHeaders
        },
        impositionDieCutters() {
            return this.imposition.impositionDieCutters
        },
        dieCutters() {
            return this.$store.state.jobs.jobs.dieCutters
        },
        dieCuttersOwnershipTypes() {
            return this.$store.state.configurations.tools.dieCutters.ownershipTypes
        },
        hasData() {
            return this.task && Object.keys(this.task).length !== 0 && this.task.process != null
        },
        printTask() {
            return this.imposition.impositionTask.find(task => task.processId === appConfig.common.processes.printTaskId)
        },
        isSlicingAfterPrintingTask() {
            return this.task.processId === appConfig.common.processes.slicingAfterPrintingTaskId
        },
        isSlicingAfterFinishingTask() {
            return this.task.processId === appConfig.common.processes.slicingAfterFinishingTaskId
        },
        isSlicingAfterDieCuttingTask() {
            return this.task.processId === appConfig.common.processes.slicingAfterDieCuttingTaskId
        },
        isCuttingPaperTask() {
            return this.task.processId === appConfig.common.processes.cuttingPaperTaskId
        },
        isLaminationTask() {
            return this.task.processId === appConfig.common.processes.laminationTaskId
        },
        isPackingTask() {
            return this.task.processId === appConfig.common.processes.packingTaskId
        },
        isSendingTask() {
            return this.task.processId === appConfig.common.processes.sendingTaskId
        },
        isSewnBindTask() {
            return this.task.processId === appConfig.common.processes.sewnBindTaskId
        },
        isGlueBindTask() {
            return this.task.processId === appConfig.common.processes.glueBindTaskId
        },
        isFoldingTask() {
            return this.task.processId === appConfig.common.processes.foldingTaskId
        },
        availableFoldingTypes() {
            const machine = this.task?.machine ||
                this.machines?.find(item => item.id === this.task?.machineId)

            return machine?.availableFoldingTypes ?? []
        },
        isCtpTask() {
            return this.task.processId === appConfig.common.processes.ctpTaskId
        },
        isDieCuttingTask() {
            return this.task.processId === appConfig.common.processes.dieCuttingTaskId
        },
        impositionTaskPackages() {
            return this.imposition?.impositionTask
                ?.find(task => task.processId === appConfig.common.processes.packingTaskId)
                ?.impositionTaskPackages

        },
        // Walidacje
        machineErrors() {
            return this.$v.task.machineId.$invalid
        },
        sewnBindMaterialErrors() {
            return this.$v.task.impositionTaskMaterial.$invalid
        },
        glueBindMaterialErrors() {
            return this.$v.task.impositionTaskMaterial.$invalid
        },
        foldingTypeErrors() {
            return this.$v.task.foldingTypeId.$invalid
        },
        plateTypeIdErrors() {
            return this.$v.imposition.plateTypeId.$invalid
        },
        paperDescriptionErrors() {
            return this.$v.imposition.paperDescription.$invalid
        },
        taskWasteAmountErrors() {
            const errors = []

            if (!this.$v.task.wasteAmount.required || !this.$v.task.wasteAmount.integer) {
                errors.push('Proszę podać prawidłową wartość')
            }

            return errors
        },
        taskWastePercentErrors() {
            const errors = []

            if (!this.$v.task.wastePercent.required || !this.$v.task.wastePercent.between || !this.$v.task.wastePercent.fourDigitsAfterDecimal) {
                errors.push('Proszę podać prawidłową wartość z zakresu 0-25')
            }

            return errors
        },
    },
    watch: {
        impositionData: {
            handler: function(newValue) {
                this.imposition = JSON.parse(JSON.stringify(newValue))
                this.plateTypes = JSON.parse(JSON.stringify(this.plateTypesData))

                if (this.isDieCuttingTask && this.imposition?.impositionDieCutters.length > 0) {
                    this.formattedImposition()
                }
            },
            deep: true
        },
        taskData: {
            handler: function(newValue) {
                this.task = JSON.parse(JSON.stringify(newValue))
                this.enrichTaskMachine()
            },
            deep: true
        },
        machines() {
            this.enrichTaskMachine()
        },
        isOpen: {
            immediate: true,
            async handler(newValue) {
                if (!newValue || (!this.isSewnBindTask && !this.isGlueBindTask)) return

                const response = await this.$store.dispatch('configurations/getMaterials', {
                    query: `?isVisible=true&productionProcess_id=${this.taskData.processId}`,
                    shouldSavedInStore: false
                })

                this.materials = response.data.results

                if (this.task?.impositionTaskMaterial?.[0]?.materialId) {
                    this.selectedMaterial = this.materials.find(
                        material => material.id === this.task.impositionTaskMaterial[0].materialId
                    )
                }
            }
        },
        task: {
            immediate: true,
            deep: true,
            handler(newTask, oldTask) {
                if (!this.materials.length) return

                const newMaterialId = newTask.impositionTaskMaterial?.[0]?.materialId
                const oldMaterialId = oldTask?.impositionTaskMaterial?.[0]?.materialId

                if (newMaterialId !== oldMaterialId) {
                    this.selectedMaterial = newMaterialId
                        ? this.materials.find(material => material.id === newMaterialId)
                        : null
                }

                if (newTask.impositionTaskMaterial == null) {
                    this.$set(newTask, 'impositionTaskMaterial', [])
                }
            }
        }
    },
    mounted() {
        this.getDeliveryMethods()

    },
    methods: {
        isCheckboxProperty(property) {
            return property.productionProcessProperty.propertyType?.id === appConfig.common.productionProcessPropertyTypes.checkbox
        },
        parseCheckboxValue(value) {
            return value === 'true'
        },
        changeCheckboxValue(property, value) {
            property.value = value ? 'true' : 'false'
        },
        getPropertyIndex(property) {
            return this.task.impositionTaskProperties.findIndex(prop => prop.id === property.id)
        },
        updateMaterial(material) {
            if (material) {
                const materialWithId = {
                    ...material,
                    materialId: material.id,
                    id: this.task.impositionTaskMaterial?.[0]?.id
                }

                this.$set(this.task, 'impositionTaskMaterial', [materialWithId])
            } else {
                this.$set(this.task, 'impositionTaskMaterial', [])
            }
        },
        formattedImposition() {
            this.imposition.impositionDieCutters.forEach(dieCutter => {
                if (!dieCutter.formatA) dieCutter.formatA = +this.imposition.formatA || 0
                if (!dieCutter.formatB) dieCutter.formatB = +this.imposition.formatB || 0
                dieCutter.price = this.formatPrice(dieCutter.price || 0)
            })
        },
        isDieCutterDisabled(dieCutterIndex) {
            return this.impositionDieCutters[dieCutterIndex].owner === 2 // Wykrojnik klienta
        },
        isNewDieCutter(dieCutterIndex) {
            return this.impositionDieCutters[dieCutterIndex].owner === 3 // Nowy wykrojnik
        },
        getLabelWithIndex(index, label) {
            return `${index + 1}. ${label}`
        },
        dieCutterOwnerErrors(index) {
            if (!this.isJobEditable) return

            return this.$v.imposition.impositionDieCutters.$each[index].owner.$invalid
        },
        numberDieCutterErrors(index) {
            if (!this.isJobEditable) return

            return this.$v.imposition.impositionDieCutters.$each[index].dieCutter.$invalid
        },
        impositionDieCutterFormatAErrors(index) {
            if (!this.isJobEditable) return

            return this.$v.imposition.impositionDieCutters.$each[index].formatA.$invalid
        },
        impositionDieCutterFormatBErrors(index) {
            if (!this.isJobEditable) return

            return this.$v.imposition.impositionDieCutters.$each[index].formatB.$invalid
        },
        impositionDieCutterFormatGreater(index) {
            return this.impositionDieCutterFormatBErrors(index) || this.impositionDieCutterFormatAErrors(index)
        },
        impositionDieCutterPartAmountErrors(index) {
            if (!this.isJobEditable) return

            return this.$v.imposition.impositionDieCutters.$each[index].partAmount.$invalid
        },
        impositionDieCutterPriceErrors(index) {
            if (!this.isJobEditable) return

            return this.$v.imposition.impositionDieCutters.$each[index].price.$invalid
        },
        impositionFormatAErrors() {
            if (!this.isJobEditable) return

            return this.$v.imposition.formatA.$invalid
        },
        impositionFormatBErrors() {
            if (!this.isJobEditable) return

            return this.$v.imposition.formatB.$invalid
        },
        addDieCutter() {
            this.imposition.impositionDieCutters.push({
                imposition: this.imposition.id,
                formatA: null,
                formatB: null,
                partAmount: null,
                price: this.formatPrice(0)
            })
        },
        removeDieCutter(index) {
            this.imposition.impositionDieCutters.splice(index, 1)
        },
        setDieCutterOwner(index, value) {
            if (!value) {
                this.imposition.impositionDieCutters[index].owner = null

                return
            }

            this.imposition.impositionDieCutters[index].owner = value?.id

            if (value?.id !== 1) { // if diecutter type not from stock
                this.imposition.impositionDieCutters[index].dieCutter = null
            } else if (value?.id !== 3) { // if diecutter type not new
                this.imposition.impositionDieCutters[index].newDieCutterSymbol = null
            }
            this.imposition.impositionDieCutters[index].price = this.formatPrice(0)
        },
        setDieCutterType(index, value) {
            this.impositionDieCutters[index].dieCutter = value?.id
            this.$v.imposition.impositionDieCutters.$each.$iter[index].dieCutter.$touch()
        },
        setDieCutterPrice(index, value) {
            let formattedValue = Number(value.replace(',', '.'))

            if (!isNaN(formattedValue)) {
                this.impositionDieCutters[index].price = formattedValue.toFixed(2).replace('.', ',')
            } else {
                this.impositionDieCutters[index].price = value
            }
            this.$forceUpdate()
        },
        inputDieCutterPrice(index, value) {
            if (
                (!twoDigitsAfterDecimalWithComma(value) && !twoDigitsAfterDecimal(value)) ||
                value === undefined || value === null || value === ''
            ) {
                this.impositionDieCutters[index].price = value
            } else {
                let formattedValue = Number(value).toString().replace('.', ',')

                this.impositionDieCutters[index].price = isNaN(formattedValue) ? value : formattedValue
            }
            this.$forceUpdate()
        },
        isDedicatedDistantTransportType() {
            const transportTaskProperty = this.getTransportTaskProperty()
            const dedicatedDistantTransportTypes = transportTaskProperty.productionProcessProperty.productionProcessPropertyValues.filter(
                item => (item.fixedValue === null && item.factor !== null)
            )
            const result = dedicatedDistantTransportTypes.map(element => element.name)

            return result.includes(transportTaskProperty.value)
        },
        distanceSuffix() {
            return this.isDedicatedDistantTransportType() ? 'km' : ''
        },
        getTransportTaskProperty() {
            return this.task.impositionTaskProperties.find(item => item.productionProcessPropertyId === 10)
        },
        getTransportDistanceTaskProperty() {
            return this.task.impositionTaskProperties.find(item => item.productionProcessPropertyId === 26)
        },
        getTransportCostTaskProperty() {
            return this.task.impositionTaskProperties.find(item => item.productionProcessPropertyId === 20)
        },
        showSendingParametersDialog() {
            this.sendingParametersDialog = true
        },
        closeSendingParametersDialog() {
            this.sendingParametersDialog = false
        },
        saveSendingParametersDialog(newTaskData) {
            this.task = newTaskData
            this.closeSendingParametersDialog()
        },
        enrichTaskMachine() {
            if (!this.task?.machineId || !this.machines?.length) return

            const machineFromList = this.machines.find(item => item.id === this.task.machineId)

            if (machineFromList) {
                this.task.machine = machineFromList
            }
        },
        machineChanged(machine) {
            if (!machine) {
                this.task.machine = null
                this.task.machineId = null
                this.task.foldingTypeId = null

                return
            }

            this.task.machine = machine
            this.task.machineId = machine.id
            this.task.wasteAmount = machine.wasteAmount
            this.task.wastePercent = machine.wastePercent

            const currentFoldingTypeId = this.task?.foldingTypeId
            const foldingTypeExists = this.availableFoldingTypes.some(foldingType => {
                return foldingType?.id === currentFoldingTypeId
            })

            if (!foldingTypeExists) {
                this.task.foldingTypeId = null
            }
        },
        paperChanged(paper) {
            this.imposition.paperId = paper.id
        },
        isTaskParameterAvailable(task, fieldName) {
            return task.requiredFields.some(item => item.name === fieldName)
        },
        parseMaterialsValue(data) {
            const materials = data.map(item => item.material.name)

            return materials.length ? materials : null
        },
        showMaterialsDialog() {
            this.$emit('show-materials-dialog')
        },
        closeMaterialsDialog() {
            this.$emit('close-materials-dialog')
        },
        showSlicingFormatsDialog(impositionTaskOrderComponent) {
            this.$emit('show-slicing-formats-dialog', impositionTaskOrderComponent)
        },
        closeSlicingFormatsDialog() {
            this.$emit('close-slicing-formats-dialog')
        },
        onSave() {
            if (this.$v.$invalid && this.jobStatusId === 40) { // zamknięte
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            if (!this.actionLoading) {
                this.$store.dispatch('toggleCircularLoader', true)

                this.saveImposition()
                    .then(() => {
                        this.saveTask()
                    })
                    .catch(error => {
                        this.$store.dispatch('toggleCircularLoader', false)
                        errorHandler(error)
                    })
            }
        },
        async saveImposition() {
            this.$store.dispatch('toggleCircularLoader', true)

            let impositionDieCutters = this.imposition.impositionDieCutters.map(item => ({
                ...item,
                price: typeof item.price === 'string' ? +this.unformatPrice(item.price) : item.price
            }))

            return await this.$store.dispatch('jobs/saveImposition', { ...this.imposition, impositionDieCutters })
                .catch(error => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    errorHandler(error)
                })
        },
        saveTask() {
            this.$store.dispatch('toggleCircularLoader', true)
            this.$store.dispatch('jobs/saveTask', this.task)
                .then(() => {
                    this.$store.dispatch('jobs/getJob', this.jobId)
                        .then(response => {
                            this.$emit('on-save', response.data)
                            this.onClose()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.$store.dispatch('toggleCircularLoader', false)
                        })
                })
                .catch(error => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    errorHandler(error)
                })
        },
        onClose() {
            if (!this.actionLoading) {
                setTimeout(() => {
                    this.imposition = JSON.parse(JSON.stringify(this.impositionData))
                    this.task = JSON.parse(JSON.stringify(this.taskData))
                    this.enrichTaskMachine()
                    this.$v.$reset()
                }, 500)

                this.$emit('on-close')
            }
        },
        filterMachinesByTask(task, machines) {
            return machines.filter(item => item.productionProcess.includes(task.processId))
        },
        parseDescriptionValue(value) {
            this.task.description = value || null
        },
        propertyErrors(propertyIndex) {
            if (!this.isJobEditable) return

            return this.$v.task.impositionTaskProperties.$each.$iter[propertyIndex].$invalid
        },
        getColumnWidth(task) {
            const MD6 = 6, MD4 = 4
            const TYPESETTING_PROCESS_ID = 131 // proces składarko-sklejarka

            return task.processId === TYPESETTING_PROCESS_ID ? MD6 : MD4
        },
        async getDeliveryMethods() {
            return await this.$store.dispatch('calculations/getDeliveryMethods')
                .then(response => {
                    this.deliveryMethods = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getDeliveryMethodName(id) {
            const deliveryMethod = this.deliveryMethods.find(item => item.id === id)

            return deliveryMethod ? deliveryMethod.name : ''
        }
    }
}
</script>
